import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
} from "@mui/material";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ReactApexChart from "react-apexcharts";
import { toastAlert } from "../../../helpers/toastAlert";
import { getOrderData } from "../../../services/dashboardService";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

const STATUS = {
  inProgress: {
    key: "inProgress",
    label: "In progress",
    color: "#2B4D8F",
    soft: "#E8EEF7",
    icon: TrendingUpRoundedIcon,
  },
  pending: {
    key: "pending",
    label: "Pending",
    color: "#D97706",
    soft: "#FEF3C7",
    icon: ScheduleRoundedIcon,
  },
  completed: {
    key: "completed",
    label: "Completed",
    color: "#059669",
    soft: "#D1FAE5",
    icon: CheckCircleOutlineRoundedIcon,
  },
};

const BookingData = (props) => {
  const [taskData, setTaskData] = useState({
    pending: 0,
    completed: 0,
    inProgress: 0,
    Total: 0,
  });

  const getTotalOrderData = async () => {
    try {
      const orderData = await getOrderData(props);
      if (orderData && orderData.data) {
        const pending = orderData.data.pending || 0;
        const completed = orderData.data.completed || 0;
        const inProgress = orderData.data.inProgress || 0;
        setTaskData({
          pending,
          completed,
          inProgress,
          Total: pending + completed + inProgress,
        });
      } else {
        toastAlert(
          orderData?.message !== undefined
            ? orderData.message
            : "Problem Fetching Records.",
          "error"
        );
      }
    } catch (error) {
      console.log("error catch block", error);
      toastAlert("Please check Internet Connection.", "error");
    }
  };

  useEffect(() => {
    getTotalOrderData();
  }, []);

  const barOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "Inter, Plus Jakarta Sans, sans-serif",
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: "42%",
        distributed: true,
      },
    },
    colors: [
      STATUS.completed.color,
      STATUS.inProgress.color,
      STATUS.pending.color,
    ],
    grid: {
      borderColor: "#E2E8F0",
      strokeDashArray: 4,
    },
    xaxis: {
      categories: ["Completed", "In progress", "Pending"],
      labels: {
        style: { colors: "#64748B", fontWeight: 600, fontSize: "12px" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        formatter: (v) => Math.round(v),
        style: { colors: "#94A3B8", fontSize: "11px" },
      },
      forceNiceScale: true,
      min: 0,
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: {
      y: { formatter: (v) => `${v} tasks` },
    },
  };

  const barSeries = [
    {
      name: "Tasks",
      data: [taskData.completed, taskData.inProgress, taskData.pending],
    },
  ];

  const pieOptions = {
    chart: {
      type: "donut",
      fontFamily: "Inter, Plus Jakarta Sans, sans-serif",
    },
    labels: ["Completed", "In progress", "Pending"],
    legend: {
      position: "bottom",
      fontWeight: 600,
      labels: { colors: "#475569" },
      markers: { width: 10, height: 10, radius: 10 },
    },
    colors: [
      STATUS.completed.color,
      STATUS.inProgress.color,
      STATUS.pending.color,
    ],
    stroke: { width: 0 },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            name: {
              show: true,
              offsetY: -6,
              color: "#64748B",
              fontSize: "13px",
              fontWeight: 600,
            },
            value: {
              show: true,
              offsetY: 4,
              color: "#0F172A",
              fontSize: "28px",
              fontWeight: 800,
              formatter: (v) => `${v}`,
            },
            total: {
              show: true,
              label: "Total tasks",
              color: "#64748B",
              fontSize: "13px",
              fontWeight: 600,
              formatter: () => `${taskData.Total || 0}`,
            },
          },
        },
      },
    },
    dataLabels: { enabled: false },
  };

  const pieSeries = [taskData.completed, taskData.inProgress, taskData.pending];

  const summaryCards = [
    { ...STATUS.inProgress, value: taskData.inProgress },
    { ...STATUS.pending, value: taskData.pending },
    { ...STATUS.completed, value: taskData.completed },
  ];

  return (
    <Box
      className="card-main"
      sx={{
        px: { xs: 2, md: 2.5 },
        py: { xs: 2, md: 2.5 },
        borderRadius: "16px",
        border: "1px solid #E2E8F0",
        background: "#FFFFFF",
        boxShadow: "0 8px 24px rgba(26, 51, 102, 0.05)",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 800,
              color: "#0F172A",
              letterSpacing: "-0.02em",
            }}
          >
            Task overview
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748B" }}>
            Live snapshot of work across pending, active, and completed tasks.
          </Typography>
        </Box>
        <Chip
          label={`${taskData.Total} total`}
          sx={{
            bgcolor: "var(--thm-color-soft)",
            color: "var(--thm-color)",
            fontWeight: 700,
            borderRadius: "999px",
          }}
        />
      </Stack>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={3}>
          <Stack spacing={1.5}>
            {summaryCards.map((item) => {
              const Icon = item.icon;
              return (
                <Box
                  key={item.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.75,
                    borderRadius: "14px",
                    border: "1px solid #E2E8F0",
                    background: item.soft,
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "12px",
                      bgcolor: "#fff",
                      display: "grid",
                      placeItems: "center",
                      color: item.color,
                      flexShrink: 0,
                    }}
                  >
                    <Icon fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#64748B",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 26,
                        fontWeight: 800,
                        color: "#0F172A",
                        lineHeight: 1.1,
                        mt: 0.25,
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: "14px",
              border: "1px solid #E2E8F0",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#0F172A",
                  mb: 1,
                }}
              >
                Tasks by status
              </Typography>
              <ReactApexChart
                options={barOptions}
                series={barSeries}
                type="bar"
                height={250}
                width="100%"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: "14px",
              border: "1px solid #E2E8F0",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: 2.25, "&:last-child": { pb: 2.25 } }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#0F172A",
                  mb: 1,
                }}
              >
                Task mix
              </Typography>
              <ReactApexChart
                options={pieOptions}
                series={pieSeries}
                type="donut"
                height={265}
                width="100%"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  authData: state.authReducer,
});

export default withRouter(connect(mapStateToProps)(BookingData));
